// 공고문 분류 고정값 (LLM 분류 + UI 드롭다운 공용).

// 분야
export const CATEGORIES = [
  "금융",
  "기술",
  "인력",
  "수출",
  "내수",
  "창업",
  "경영",
  "기타",
] as const;

// category2(14종 신 분야) → category(8종 구 분야) 파생 매핑.
//   LLM 이 category(분야)를 '기타'로 과다 분류하는 문제 보정 — category2 가 명확하면 그걸로 분야를 정한다.
//   매핑 없는 category2(문화·환경·시설·농림·복지 등)는 구 분야에 대응값이 없어 '기타' 유지.
export const CATEGORY2_TO_CATEGORY: Record<string, (typeof CATEGORIES)[number]> = {
  "R&D·기술·디지털": "기술",
  "디자인·브랜드·IP": "기술",
  "수출·판로·마케팅": "수출",
  "창업·벤처": "창업",
  "인력·고용·일자리": "인력",
  "자금·금융": "금융",
  "컨설팅·멘토링·교육": "경영",
  "일반기업지원(대상만)": "경영",
};
// 분야(category) 확정 — LLM category 가 명확(기타/빈값 아님)하면 유지, 아니면 category2 로 파생.
export function deriveCategory(
  category: string | null | undefined,
  category2: string | null | undefined,
): string | null {
  if (category && category !== "기타") return category;
  const mapped = category2 ? CATEGORY2_TO_CATEGORY[category2] : undefined;
  return mapped ?? category ?? null;
}

// 1차 분류(제목 기반 "유형") — 8종
export const CATEGORY1 = [
  "지원사업",
  "행사·네트워킹",
  "교육·설명회",
  "공모전·경진",
  "절차·결과 공지",
  "채용·인력",
  "입찰·용역·고시",
  "기타·공지",
] as const;

// 2차 분류(키워드/내용 기반 "분야") — 14종
export const CATEGORY2 = [
  "R&D·기술·디지털",
  "디자인·브랜드·IP",
  "수출·판로·마케팅",
  "창업·벤처",
  "문화·콘텐츠·관광",
  "컨설팅·멘토링·교육",
  "일반기업지원(대상만)",
  "인력·고용·일자리",
  "자금·금융",
  "환경·에너지·안전",
  "시설·입주·공간",
  "농림·수산·식품",
  "복지·사회·공익",
  "기타",
] as const;

// 1차 분류 "지원사업"의 2depth 세부유형 (자료.md). /programs 상단 칩 + category1Sub 필드.
export const CATEGORY1_SUB = [
  "사업·프로그램",
  "인증·평가·지정",
  "문화·예술·콘텐츠",
  "지역·균형·정주",
  "환경·에너지·안전",
  "네트워크·협력·매칭",
  "복지·사회·공익",
  "농림·수산·식품",
  "행정·운영·기금",
  "디자인·브랜드·IP",
  "마케팅·수출·판로",
  "입주·공간·시설",
  "R&D·기술개발",
  "창업·육성",
  "자금·금융",
  "인력·고용",
  "컨설팅·멘토링",
  "기타",
] as const;

// category1="지원사업" 일 때 세부유형(category1Sub) 분류 가이드.
export const CATEGORY1_SUB_RULES = `[category1Sub — "지원사업"의 세부유형: 사업의 성격/영역으로 아래 중 택1]
- 사업·프로그램: 특정 분야로 안 갈리는 일반 지원사업/프로그램/육성/모집.
- 인증·평가·지정: 인증/평가/지정/확인/선정 등 자격·지위 부여.
- 문화·예술·콘텐츠: 문화/예술/공연/콘텐츠/관광.
- 지역·균형·정주: 지역/균형발전/정주여건/귀농귀촌/정착.
- 환경·에너지·안전: 환경/탄소/에너지/친환경/안전/재난.
- 네트워크·협력·매칭: 네트워킹/협력/매칭/상담회/교류/오픈이노베이션.
- 복지·사회·공익: 복지/취약계층/사회적경제/공익/비영리.
- 농림·수산·식품: 농업/농촌/수산/축산/식품.
- 행정·운영·기금: 행정/운영/기금/보조금 집행 등 행정성.
- 디자인·브랜드·IP: 디자인/브랜드/특허/상표/지식재산.
- 마케팅·수출·판로: 수출/해외/판로/마케팅/입점/박람회.
- 입주·공간·시설: 입주/공간/시설/센터/임대.
- R&D·기술개발: 기술개발/연구/실증/AI/디지털/스마트/시제품.
- 창업·육성: 창업/스타트업/벤처/사업화/액셀러레이팅.
- 자금·금융: 자금/융자/보증/바우처/대출/펀드.
- 인력·고용: 인력/고용/일자리/인건비/인턴.
- 컨설팅·멘토링: 컨설팅/멘토링/코칭/진단/자문.
- 기타: 위로 분류 어려움.`;

// category1/category2 분류 가이드 (추출 프롬프트 + 분류기 공용).
export const CATEGORY_CLASSIFY_RULES = `[category1 — "유형": 주로 공고 '제목'으로 판단, 아래 중 택1]
- 지원사업: 기업/개인에게 자금·서비스·기회를 지원하는 사업(모집/지원/바우처/융자/입주/육성 등). 대부분 해당.
- 행사·네트워킹: 전시회/박람회/페어/포럼/데모데이/IR/상담회/교류회 등 행사 참가·개최.
- 교육·설명회: 교육/아카데미/워크숍/특강/설명회/연수/역량강화.
- 공모전·경진: 공모전/경진대회/해커톤/챌린지/콘테스트/아이디어 공모.
- 절차·결과 공지: 선정결과/합격자/변경·연장·재공고/취소·정정/회의록 등 절차성 공지.
- 채용·인력: 직원/인턴/위촉/평가위원 등 사람 채용·모집(지원사업 참여기업 모집은 제외).
- 입찰·용역·고시: 입찰/용역/제안요청(RFP)/계약/행정고시/입법예고 등.
- 기타·공지: 위 어디에도 안 맞는 행정/안내 공지.

[category2 — "분야": 주로 '키워드/내용'으로 판단, 유형과 무관하게 아래 중 택1]
- R&D·기술·디지털: 기술개발/연구/실증/AI/디지털/스마트공장/제조DX/시제품.
- 디자인·브랜드·IP: 디자인/브랜드/특허/지식재산/상표/IP.
- 수출·판로·마케팅: 수출/해외/글로벌/판로/마케팅/입점/박람회.
- 창업·벤처: 창업/스타트업/벤처/사업화/액셀러레이팅.
- 문화·콘텐츠·관광: 콘텐츠/문화/예술/공연/게임/웹툰/영상/관광.
- 컨설팅·멘토링·교육: 컨설팅/멘토링/코칭/진단/교육/양성.
- 일반기업지원(대상만): 분야 특정 없이 중소기업/소상공인 등 '대상'만 드러나는 일반 기업지원.
- 인력·고용·일자리: 인력/고용/일자리/인건비/채용연계/인턴.
- 자금·금융: 자금/융자/보증/바우처/보조금/대출/펀드.
- 환경·에너지·안전: 환경/에너지/탄소/친환경/안전.
- 시설·입주·공간: 입주/공간/시설/임대/센터.
- 농림·수산·식품: 농업/농촌/수산/식품/축산.
- 복지·사회·공익: 복지/취약계층/장애/청소년/여성/사회적경제/비영리.
- 기타: 위 분야로 분류 어려움.`;

// 지원대상 업체분류 (복수 선택 가능 — 한 공고가 여러 유형을 대상으로 할 수 있음)
export const TARGET_BUSINESS_TYPES = [
  "예비창업자",
  "창업기업",
  "소상공인",
  "중소기업",
  "중견기업",
  "대기업",
  "사회적경제기업", // 사회적기업·협동조합·소셜벤처 등
  "비영리단체", // 협회·재단·법인 등
  "대학·연구기관",
  "농어업인", // 농가·농민·어가·어민·임업인·축산·양봉·귀농/귀어 (1차산업 생산자 — 기업·일반개인과 구분)
  "개인", // 일반 국민·구직자·청년 등 (기업 아님)
  "기타",
] as const;

// 대상 사업자 형태(법적 형태) — 가입 프로필 기업형태와 매칭용. TARGET_BUSINESS_TYPES(규모)와 별개 축.
//   공고에 형태 명시가 없으면(형태 무관) 빈 배열.
export const TARGET_COMPANY_TYPES = [
  "법인",       // 법인사업자
  "개인사업자",  // 개인사업자(개인 명의 사업자등록)
  "예비창업자",  // 사업자 미등록 · 창업 예정
] as const;

// 대상 업종(신청 기업의 사업 분야) — 가입 프로필 업종과 매칭용. category2(지원 분야)와 다른 축.
//   공고가 특정 업종 기업만 대상으로 할 때만 채움. 전 업종 대상(업종 무관)이면 빈 배열. (기타=빈 배열로 표현)
export const TARGET_INDUSTRIES = [
  "IT·소프트웨어",
  "제조",
  "바이오·의료",
  "그린·에너지",
  "문화·콘텐츠",
  "유통·서비스",
  "건설·부동산",
  "농림·수산·식품",
] as const;

// 요구/우대 인증 — 가입 프로필 보유인증과 매칭용. 공고 자격요건 또는 우대사항에 인증이 언급될 때만 채움.
//   인증 조건이 없으면 빈 배열. (가입 CERTS 와 정렬 — '해당 없음'은 사용자 자기신고라 제외)
export const TARGET_CERTIFICATIONS = [
  "벤처기업",
  "이노비즈",
  "메인비즈",
  "ISO",
] as const;

// 대상 특성 — "이 사업의 대상이 누구인가"(대표자·기업 속성). 가입 프로필 매칭용(중복 허용).
//   공고가 특정 특성 대상을 명시/우대하면 해당 코드를 모두 발췌. 특성 제한이 없으면 빈 배열.
export const TARGET_GROUPS = [
  "women",       // 여성기업
  "youth",       // 청년(대표자 연령)
  "senior",      // 중장년·시니어(대표자 연령)
  "small",       // 소상공인(규모)
  "disabled",    // 장애인기업
  "social_ent",  // 사회적기업
  "restart",     // 재창업(폐업 이력)
] as const;
export const TARGET_GROUP_LABEL: Record<string, string> = {
  women: "여성기업",
  youth: "청년",
  senior: "중장년·시니어",
  small: "소상공인",
  disabled: "장애인기업",
  social_ent: "사회적기업",
  restart: "재창업",
};

// 도메인(산업/영역) — 공고문 키워드/제목을 대표 키워드로 매칭해 파생. /programs 상단 필터용.
//   code=DB 저장·필터값, label=표시명, keywords=매칭 대표 키워드.
export const DOMAINS = [
  { code: "agri", label: "농림·축수산·식품", keywords: ["스마트팜", "농업", "농산물", "농식품", "식품", "푸드테크", "수산", "어업", "양식", "해양수산", "귀농", "귀어", "청년농", "축산", "임업", "산림", "농촌", "영농"] },
  // '플랫폼' 제거(2026-11-05): 범용 '기반/허브' 의미로 바이오·농업·콘텐츠·유통·창업 플랫폼까지 ai 로 과대수집(492건 오탐). AI 프로그램은 AI/SW/ICT 등으로 매칭.
  { code: "ai", label: "AI·SW·ICT", keywords: ["AI", "인공지능", "머신러닝", "딥러닝", "생성형", "LLM", "SW", "소프트웨어", "핀테크", "디지털전환", "DX", "빅데이터", "데이터바우처", "클라우드", "SaaS", "XR", "메타버스", "블록체인", "정보보호", "ICT", "IoT"] },
  // '친환경'(수식어로 전 업종에 범용)·'ESG'(지배구조·경영 일반)는 섬유·철강·수산·사회적경제 등 타 도메인 글을 green 으로 과대수집해 제거(2026-11-05). 진짜 에너지·환경은 재생에너지·태양광·수소·탄소중립 등으로 매칭.
  { code: "green", label: "에너지·환경·기후", keywords: ["재생에너지", "신재생", "신에너지", "탄소중립", "넷제로", "대기오염", "대기질", "수질", "환경오염", "자원순환", "폐기물", "이차전지", "배터리", "태양광", "풍력", "수소", "기후", "에너지효율"] },
  // '산업단지' 제거·'부품' 정밀화(2026-11-05): 산업단지=구조고도화·계획 주민설명회 등 행정노이즈. bare '부품'은 자동차부품(adv)·교육/상담회 등 과대 → 제거하고 '부품국산화·부품개발·핵심부품·소재부품장비' 로 좁혀 진짜 소부장(부품국산화 등) 보존.
  { code: "mfg", label: "제조·소부장", keywords: ["스마트공장", "스마트제조", "제조DX", "제조업", "소재부품", "소재·부품", "소재부품장비", "소부장", "부품국산화", "부품개발", "핵심부품", "소공인", "철강", "화학섬유", "섬유", "뿌리기술", "뿌리산업", "금형", "정밀가공", "주조", "표면처리"] },
  { code: "content", label: "콘텐츠·미디어·문화예술", keywords: ["공연", "문화예술", "예술", "영상", "방송", "숏폼", "웹툰", "캐릭터", "출판", "게임", "이스포츠", "콘텐츠", "미디어", "애니메이션", "음악", "영화", "공예"] },
  { code: "adv", label: "첨단모빌리티·로봇·반도체·방산", keywords: ["미래차", "자동차부품", "반도체", "시스템반도체", "팹리스", "디스플레이", "로봇", "방산", "국방", "드론", "우주항공", "항공우주", "위성", "조선", "모빌리티", "자율주행", "전기차", "UAM", "양자"] },
  { code: "bio", label: "바이오·헬스·의료", keywords: ["제약", "신약", "바이오", "바이오헬스", "의료서비스", "의료기관", "의료기기", "디지털헬스", "헬스케어", "의료", "백신", "체외진단", "재생의료"] },
  { code: "commerce", label: "유통·커머스·물류", keywords: ["물류", "무역", "수출입", "입점", "홈쇼핑", "이커머스", "라이브커머스", "커머스", "유통", "팝업스토어", "장터"] },
  { code: "tourism", label: "관광·레저·스포츠", keywords: ["여행", "관광", "관광벤처", "축제", "MICE", "스포츠", "레저"] },
  { code: "food", label: "외식·상권·생활서비스", keywords: ["음식점", "외식", "골목상권", "상권활성화", "상권", "전통시장", "상점가", "프랜차이즈", "자영업", "미용", "세탁"] },
  { code: "care", label: "돌봄·사회서비스", keywords: ["보육", "아이돌봄", "돌봄", "노인돌봄", "사회복지", "사회서비스", "복지서비스"] },
  { code: "beauty", label: "뷰티·패션·생활소비재", keywords: ["화장품", "뷰티", "패션", "의류", "신발", "반려동물", "주얼리", "생활용품", "소비재"] },
  { code: "build", label: "건설·부동산·도시", keywords: ["건축", "리모델링", "인테리어", "건설", "시공", "부동산", "도시재생", "스마트시티"] },
] as const;

// 대상 업종(target_industries) → 산업도메인 직접 매핑. LLM 이 이미 추출한 대상 업종을 도메인 추론에 활용(고정밀 recall 보강).
export const INDUSTRY_DOMAIN: Record<string, string> = {
  "IT·소프트웨어": "ai", "제조": "mfg", "바이오·의료": "bio", "그린·에너지": "green",
  "문화·콘텐츠": "content", "유통·서비스": "commerce", "건설·부동산": "build", "농림·수산·식품": "agri",
};

export const DOMAIN_LABEL: Record<string, string> = Object.fromEntries(DOMAINS.map((d) => [d.code, d.label]));
export const DOMAIN_KEYWORDS: Record<string, readonly string[]> = Object.fromEntries(DOMAINS.map((d) => [d.code, d.keywords]));

// 공고문이 속한 도메인 코드 목록 — 저장(announcement_docs.domains, GIN 인덱스)해 도메인 필터를 고속화한다.
//   판정 규칙은 목록 필터(getProgramsPage)와 동일: 도메인 키워드가 (keywords 배열에 정확히 포함) 또는
//   (제목에 부분 포함) 되면 그 도메인에 속한다. 한 글이 여러 도메인에 속할 수 있다.
//   → 저장 후 필터는 `domains has <code>` 단일 GIN 조회로 대체(제목 ILIKE OR 스캔 제거, recall 동일).
//   recall 보강(2026-11): ①target_industries→도메인 직접매핑(L1, 고정밀) ②title 외 extraText(요약 summary3Lines)도 스캔(L2).
export function inferDomains(
  keywords: readonly string[] | null | undefined,
  title: string | null | undefined,
  targetIndustries?: readonly string[] | null,
  extraText?: string | null,
): string[] {
  const kwSet = new Set(keywords ?? []);
  // 요약(summary3Lines=extraText) 스캔 제거(2026-11-05): 요약은 서술형이라 도메인 키워드가 부수적으로 섞여
  //   '요약에만 걸린' 저정밀 오탐(상담회·CCTV·대출·임대차 등 비에너지 글이 green 등으로)을 양산했다.
  //   제목 + keywords 배열(정확) + 업종매핑만 사용(고정밀). extraText 인자는 호환 위해 유지(미사용).
  void extraText;
  const textLc = `${title ?? ""}`.toLowerCase();
  const out = new Set<string>();
  for (const dmn of DOMAINS) {
    if (dmn.keywords.some((kw) => kwSet.has(kw) || textLc.includes(kw.toLowerCase()))) out.add(dmn.code);
  }
  // L1: 대상 업종 → 도메인 매핑(고정밀)
  for (const ind of targetIndustries ?? []) { const c = INDUSTRY_DOMAIN[ind]; if (c) out.add(c); }
  return [...out];
}

// 소관부처
export const MINISTRIES = [
  "중기부",
  "산업부",
  "고용부",
  "과기부",
  "지재처",
  "기후부",
  "문체부",
  "농식품부",
  "국토부",
  "해수부",
  "복지부",
  "행안부",
  "교육부",
] as const;

// 부처 약칭 → 정식명칭
export const MINISTRY_FULL: Record<string, string> = {
  중기부: "중소벤처기업부",
  산업부: "산업통상자원부",
  고용부: "고용노동부",
  과기부: "과학기술정보통신부",
  지재처: "특허청",
  기후부: "기후에너지환경부",
  문체부: "문화체육관광부",
  농식품부: "농림축산식품부",
  국토부: "국토교통부",
  해수부: "해양수산부",
  복지부: "보건복지부",
  행안부: "행정안전부",
  교육부: "교육부",
};

// 지자체 약칭 → 정식명칭
export const LOCAL_GOV_FULL: Record<string, string> = {
  서울: "서울특별시",
  부산: "부산광역시",
  대구: "대구광역시",
  인천: "인천광역시",
  대전: "대전광역시",
  울산: "울산광역시",
  세종: "세종특별자치시",
  경기: "경기도",
  강원: "강원특별자치도",
  충북: "충청북도",
  충남: "충청남도",
  전북: "전북특별자치도",
  // 광주+전남 통합 → 전남광주통합특별시. 구 약칭(광주/전남)·병합표기(광주전남) 모두 통합명으로 확장.
  광주: "전남광주통합특별시",
  전남: "전남광주통합특별시",
  광주전남: "전남광주통합특별시",
  경북: "경상북도",
  경남: "경상남도",
  제주: "제주특별자치도",
};

// 지자체 (광역시도)
export const LOCAL_GOVS = [
  // DB 저장값(local_gov)·LLM enum·프로필 소재지는 정식명칭. /programs 필터 노출은 단축형(REGION_OPTIONS).
  //   단축형→정식명칭은 REGION_VARIANTS 가 contains 로 매칭(예: '인천'⊂'인천광역시'). matchProfile 은 정식명칭 정확일치.
  "서울특별시",
  "부산광역시",
  "대구광역시",
  "인천광역시",
  "대전광역시",
  "울산광역시",
  "세종특별자치시",
  "경기도",
  "강원특별자치도",
  "충청북도",
  "충청남도",
  "전북특별자치도",
  "전남광주통합특별시", // 광주·전남 통합 권역(구 '광주','전남' 통합). /programs 필터 노출 라벨은 '전남광주'.
  "경상북도",
  "경상남도",
  "제주특별자치도",
] as const;

// 회원 프로필/가입 '소재지' 선택지 — 지역 무관(전국) 포함. (doc localGov LLM enum 인 LOCAL_GOVS 는 건드리지 않음:
//   공고 분류는 전국/중앙부처면 null 이라 '전국' 값을 추가하면 분류가 흐트러짐.)
export const PROFILE_REGIONS = ["전국", ...LOCAL_GOVS] as const;
